import React, { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import ClientsIconFilled from "../../assets/img/Sidebar/Repositioning/Customer_filled.svg";
import ClientsIconLine from "../../assets/img/Sidebar/Repositioning/Customer_line.svg";
import InvoiceIconFilled from "../../assets/img/Sidebar/Repositioning/Invoice_Filled.svg";
import InvoiceIconLine from "../../assets/img/Sidebar/Repositioning/Invoice_Line.svg";
import SchedulesIconFilled from "../../assets/img/Sidebar/Repositioning/Scheduling_Filled.svg";
import SchedulesIconLine from "../../assets/img/Sidebar/Repositioning/Scheduling_Line.svg";
import JobIconFilled from "../../assets/img/Sidebar/Repositioning/Task_Filled.svg";
import JobIconLine from "../../assets/img/Sidebar/Repositioning/Task_Line.svg";

const PanelContainer = styled.div`
  position: fixed;
  min-width: 326px;
  width: max-content;
  background: ${({ theme }) => theme.colors.main.white};
  box-shadow: 0 8px 24px rgba(102, 110, 255, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #2266ef;
  border-radius: 8px;
  padding: 2px;
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 0;
  animation: fadeInRight 0.16s ease-out;

  @keyframes fadeInRight {
    from {
      opacity: 0;
      transform: translateX(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const ActionIconWrapper = styled.div`
  width: 20px;
  height: 20px;
  position: relative;
  flex-shrink: 0;
`;

const LineIcon = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.12s ease-in-out;
  opacity: 1;
  filter: brightness(0) saturate(100%) invert(27%) sepia(76%) saturate(2000%)
    hue-rotate(203deg);
`;

const FilledIcon = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.12s ease-in-out;
  opacity: 0;
  filter: brightness(0) saturate(100%) invert(27%) sepia(76%) saturate(2000%)
    hue-rotate(203deg);
`;

const ActionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 10px;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
  border-radius: 8px;
  flex: 1;
  min-width: 0;

  &:hover {
    background: ${({ theme }) => theme.sidebar.hoverBgColor || "#f6f8f9"};
  }

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.sidebar.hoverBgColor || "#f6f8f9"};
  }

  &:hover ${FilledIcon} {
    opacity: 1;
  }

  &:hover ${LineIcon} {
    opacity: 0;
  }
`;

const ActionText = styled.span`
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeights.medium || 500};
  color: #06100f;
  margin-top: 5px;
  text-align: center;
  white-space: nowrap;
`;

const SeparatorLine = styled.div`
  width: 1px;
  height: 65px;
  background-color: #e9f0fe;
  margin: 0px;
  flex-shrink: 0;
`;

const QuickActionsPanel = ({
  isOpen,
  onClose,
  selectedOrganization,
  anchorRef,
}) => {
  const history = useHistory();
  const panelRef = useRef(null);
  const orgType = selectedOrganization?.type || "operation";

  // Calculate position relative to anchor element
  useEffect(() => {
    if (!isOpen || !anchorRef?.current || !panelRef?.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const panelRect = panelRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Position to the right of the sidebar item
    let left = anchorRect.right + 8;
    let top = anchorRect.top;

    // Ensure panel stays within viewport bounds
    if (left + panelRect.width > viewportWidth) {
      // If panel would overflow right, position to the left instead
      left = anchorRect.left - panelRect.width - 8;
    }

    // Adjust vertical position if panel would overflow bottom
    if (top + panelRect.height > viewportHeight) {
      top = viewportHeight - panelRect.height - 8;
    }

    // Ensure panel doesn't go above viewport
    if (top < 8) {
      top = 8;
    }

    // Apply position
    panelRef.current.style.left = `${left}px`;
    panelRef.current.style.top = `${top}px`;
  }, [isOpen, anchorRef]);

  // Handle click outside and Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = event => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = event => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        anchorRef?.current &&
        !anchorRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  const handleActionClick = path => {
    onClose();
    history.push(path);
  };

  const actions = [
    {
      name: orgType === "service" ? "Create Job" : "Create Task",
      iconLine: JobIconLine,
      iconFilled: JobIconFilled,
      path: orgType === "service" ? "/user/create-job" : "/user/create-task",
      visible: true,
    },
    {
      name: "Scheduling",
      iconLine: SchedulesIconLine,
      iconFilled: SchedulesIconFilled,
      path: "/user/shift-and-schedule",
      visible: true,
    },
    {
      name: "Customers",
      iconLine: ClientsIconLine,
      iconFilled: ClientsIconFilled,
      path: "/user/add-customer",
      visible: orgType === "service",
    },
    {
      name: "Invoice",
      iconLine: InvoiceIconLine,
      iconFilled: InvoiceIconFilled,
      path: "/user/create-invoice",
      visible: orgType === "service",
    },
  ];

  const visibleActions = actions.filter(action => action.visible);

  return (
    <PanelContainer ref={panelRef} left={0} top={0}>
      {visibleActions.map((action, index) => (
        <React.Fragment key={action.name}>
          <ActionButton onClick={() => handleActionClick(action.path)}>
            <ActionIconWrapper>
              <LineIcon src={action.iconLine} alt={action.name} />
              <FilledIcon src={action.iconFilled} alt={action.name} />
            </ActionIconWrapper>
            <ActionText>{action.name}</ActionText>
          </ActionButton>
          {index < visibleActions.length - 1 && <SeparatorLine />}
        </React.Fragment>
      ))}
    </PanelContainer>
  );
};

export default QuickActionsPanel;
